import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserIcon, BellIcon, CalendarIcon, CreditCardIcon, PlusIcon, MessageSquareIcon, FileTextIcon, UsersIcon, UserPlusIcon } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
import { WhatsAppStatusWidget } from '../components/WhatsAppStatusWidget';
import { ActionItemCard } from '../components/dashboard/ActionItemCard';
import { RevenueCard } from '../components/dashboard/RevenueCard';
import { QuickActionButton } from '../components/dashboard/QuickActionButton';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { useProfile } from '../hooks/useProfile';

export function Dashboard() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { data: stats, isLoading, error, refetch } = useDashboardStats();

  // Get formatted date
  const getFormattedDate = () => {
    const date = new Date();
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return date.toLocaleDateString('en-IN', options);
  };

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] pb-20">
        <div className="bg-white shadow-sm p-6">
          <h1 className="text-xl font-semibold text-gray-900">Dashboard</h1>
        </div>
        <div className="p-4">
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Unable to Load Dashboard
            </h3>
            <p className="text-gray-600 mb-4">
              Failed to fetch dashboard statistics. Please try again.
            </p>
            <button
              onClick={() => refetch()}
              className="bg-green-600 text-white px-6 py-2 rounded-xl font-medium hover:bg-green-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  // Loading skeleton
  if (isLoading || !stats) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] pb-20">
        <div className="bg-white shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div className="space-y-2 flex-1">
              <div className="h-6 w-48 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-64 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse" />
              <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse" />
            </div>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <div className="h-32 bg-white rounded-2xl shadow-sm animate-pulse" />
          <div className="h-48 bg-white rounded-2xl shadow-sm animate-pulse" />
          <div className="h-64 bg-white rounded-2xl shadow-sm animate-pulse" />
        </div>
        <BottomNav />
      </div>
    );
  }

  const totalClients = stats.clientPipeline.active + stats.clientPipeline.inactive;

  return (
    <div className="min-h-screen bg-[#F5F5F5] pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-gray-900">
              Welcome back, {profile?.name || 'Guest'}!
            </h1>
            <p className="text-sm text-gray-600 mt-1">{getFormattedDate()}</p>
          </div>
          <div className="flex items-center gap-2">
            <WhatsAppStatusWidget compact />
            <button
              onClick={() => navigate('/profile')}
              className="w-10 h-10 bg-[#5B8E87]/10 rounded-full flex items-center justify-center hover:bg-[#5B8E87]/20 transition-colors flex-shrink-0"
              aria-label="Profile"
            >
              <UserIcon className="w-5 h-5 text-[#5B8E87]" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Today's Action Items */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Today's Action Items</h2>
          <div className="grid grid-cols-3 gap-3">
            <ActionItemCard
              title="Reminders"
              icon={<BellIcon className="w-5 h-5" />}
              todayCount={stats.actionItems.reminders.today}
              overdueCount={stats.actionItems.reminders.overdue}
              onClick={() => navigate('/schedule?tab=reminders&filter=reminders')}
            />
            <ActionItemCard
              title="Appointments"
              icon={<CalendarIcon className="w-5 h-5" />}
              todayCount={stats.actionItems.appointments.today}
              overdueCount={stats.actionItems.appointments.overdue}
              onClick={() => navigate('/schedule?tab=appointments')}
            />
            <ActionItemCard
              title="Payments"
              icon={<CreditCardIcon className="w-5 h-5" />}
              todayCount={stats.actionItems.payments.dueToday}
              overdueCount={stats.actionItems.payments.overdue}
              overdueAmount={stats.revenue.overdue.amount}
              onClick={() => navigate('/schedule?tab=reminders&filter=payments')}
            />
          </div>
          <p className="text-xs text-gray-500 text-center mt-2">
            Tap any card to view and take action
          </p>
        </section>

        {/* Leads & Clients - Side by side on desktop */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Leads & Clients</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Leads Card */}
            <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <UserPlusIcon className="w-4 h-4 text-green-600" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900">Leads</h3>
                </div>
                <div className="text-xl font-bold text-gray-900">{stats.clientPipeline.leads}</div>
              </div>

              {/* Temperature breakdown */}
              <div className="bg-gray-50 rounded-xl p-2.5 mb-3">
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div>
                    <div className="text-sm mb-0.5">✨</div>
                    <div className="text-sm font-bold text-gray-900">{stats.clientPipeline.leadTemperature.new}</div>
                    <div className="text-[10px] text-gray-500">New</div>
                  </div>
                  <div>
                    <div className="text-sm mb-0.5">🔥</div>
                    <div className="text-sm font-bold text-gray-900">{stats.clientPipeline.leadTemperature.hot}</div>
                    <div className="text-[10px] text-gray-500">Hot</div>
                  </div>
                  <div>
                    <div className="text-sm mb-0.5">🌡️</div>
                    <div className="text-sm font-bold text-gray-900">{stats.clientPipeline.leadTemperature.warm}</div>
                    <div className="text-[10px] text-gray-500">Warm</div>
                  </div>
                  <div>
                    <div className="text-sm mb-0.5">❄️</div>
                    <div className="text-sm font-bold text-gray-900">{stats.clientPipeline.leadTemperature.cold}</div>
                    <div className="text-[10px] text-gray-500">Cold</div>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/chats?audienceTab=leads')}
                className="w-full text-sm font-medium text-[#5B8E87] hover:text-[#4A7670] transition-colors"
              >
                Go to Leads →
              </button>
            </div>

            {/* Clients Card */}
            <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <UsersIcon className="w-4 h-4 text-blue-600" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900">Clients</h3>
                </div>
                <div className="text-xl font-bold text-gray-900">{totalClients}</div>
              </div>

              {/* Client status breakdown */}
              <div className="bg-gray-50 rounded-xl p-2.5 mb-3">
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div>
                    <div className="text-sm mb-0.5">🟢</div>
                    <div className="text-sm font-bold text-gray-900">{stats.clientPipeline.active}</div>
                    <div className="text-[10px] text-gray-500">Active</div>
                  </div>
                  <div>
                    <div className="text-sm mb-0.5">⚪</div>
                    <div className="text-sm font-bold text-gray-900">{stats.clientPipeline.inactive}</div>
                    <div className="text-[10px] text-gray-500">Inactive</div>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/clients')}
                className="w-full text-sm font-medium text-[#5B8E87] hover:text-[#4A7670] transition-colors"
              >
                Go to Clients →
              </button>
            </div>
          </div>
        </section>

        {/* Quick Actions - 2x2 on mobile, 4 in a row on desktop */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <QuickActionButton
              label="New Client"
              icon={<PlusIcon className="w-6 h-6" />}
              onClick={() => navigate('/add-client')}
            />
            <QuickActionButton
              label="Send Message"
              icon={<MessageSquareIcon className="w-6 h-6" />}
              onClick={() => navigate('/templates')}
            />
            <QuickActionButton
              label="New Appointment"
              icon={<CalendarIcon className="w-6 h-6" />}
              onClick={() => navigate('/schedule?tab=appointments')}
            />
            <QuickActionButton
              label="New Reminder"
              icon={<FileTextIcon className="w-6 h-6" />}
              onClick={() => navigate('/schedule?tab=reminders&filter=reminders')}
            />
          </div>
        </section>

        {/* Revenue Bar - Compact footer-style section */}
        <section>
          <RevenueCard
            revenue={stats.revenue}
            onViewDetails={() => navigate('/schedule?tab=reminders&filter=payments')}
          />
        </section>
      </div>

      {/* Keep existing BottomNav */}
      <BottomNav />
    </div>
  );
}
